<template>
<div class="ebSignInForm">
  <b-field :label="$t('common.email')"
    :type="checkEmpty(errors.email) ? '' : 'is-danger'"
    :message="checkEmpty(errors.email) ? '' : errors.email[0]">
    <b-input type="email"
      v-model="email"
      icon="envelope"
      icon-pack="fas"
      @blur="validate('email')"
      @keyup.native.enter="keyDownEnter($event)"
      :placeholder="$t('common.email')">
    </b-input>
  </b-field>

  <b-field :label="$t('common.password')"
    :type="checkEmpty(errors.password) ? '' : 'is-danger'"
    :message="checkEmpty(errors.password) ? '' : errors.password[0]">
    <b-input type="password"
      v-model="password"
      :password-reveal="true"
      icon="lock"
      icon-pack="fas"
      @blur="validate('password')"
      @keyup.native.enter="keyDownEnter($event)"
      :placeholder="$t('common.password')">
    </b-input>
  </b-field>

  <div class="field is-grouped">
    <div class="control">
      <button
        class="button is-link"
        :disabled="hasErrors"
        @click="signIn">{{ $t('common.signIn') }}</button>
    </div>
  </div>

  <div
    v-if="!isAdminPath"
    class="field is-grouped mt-4"
  >
    <div class="control">
      <router-link
        to="/email-auth"
        class="button"
      >
        <span class="icon">
          <i class="far fa-envelope"></i>
        </span>
        <span>{{ $t('form.SignInWithEmail') }}</span>
      </router-link>
    </div>
  </div>

  <div
    v-if="!isAdminPath"
    class="field is-grouped mt-4"
  >
    <div class="control">
      <button
        class="button"
        @click="signInWithOAuth('google.com')"
      >
        <span class="icon">
          <i class="fab fa-google"></i>
        </span>
        <span>{{ $t('form["Sign In with Google"]') }}</span>
      </button>
    </div>
  </div>
</div>
</template>

<script>
import {  GoogleAuthProvider } from 'firebase/auth'
import { Firebase } from '@/api'
import str from '@/util/str'
import site from '@/util/site'

export default {
  name: 'SignIn',

  props: {
    isUseAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      errors: {},
      email: '',
      password: '',
    }
  },

  computed: {
    hasErrors: function() {
      let hasError = false
      Object.keys(this.errors).map(field => {
        if (this.errors[field].length > 0) hasError = true
      })
      return hasError
    },
  },

  created: function() {
    if (this.isAdminPath && this.isAdminUser) {
      this.redirectByQueryForAdmin()
    }
  },

  methods: {
    signIn: async function() {
      this.validateAll()
      if (this.hasErrors) {
        this.showGlobalMessage(this.$t('msg["Correct inputs"]'))
        return
      }

      if (this.isAdminPath) {
        await this.signInAdmin()
      } else {
        await this.signInUser()
      }
    },

    async signInUser() {
      try {
        this.$store.dispatch('setLoading', true)
        const vals = {
          email: this.email,
          password: this.password,
        }
        const userCredential = await Firebase.signIn(vals)
        const user = site.convUserObjForStore(userCredential.user, userCredential._tokenResponse.idToken)
        this.$store.dispatch('setUser', user)
        this.$store.dispatch('setLoading', false)
        this.redirectByQueryForUser()
      } catch (err) {
        console.log(err);//!!!!!!
        this.$store.dispatch('setLoading', false)
        this.handleApiError(err, this.$t('msg["Sign In failed"]'))
      }
    },

    async signInAdmin() {
      try {
        this.$store.dispatch('setLoading', true)
        const res = await this.$cognito.signIn(this.email, this.password)
        this.$emit('email-signin-complete', 'admin')
        this.$store.dispatch('setLoading', false)
        this.redirectByQueryForAdmin()
      } catch (err) {
        this.debugOutput(err)
        this.$store.dispatch('setLoading', false)
        this.handleApiError(err, this.$t('msg["Sign In failed"]'))
      }
    },

    redirectByQueryForUser() {
      const locationTo = this.$route.query.redirect
        ? String(this.$route.query.redirect)
        : ''
      if (locationTo) {
        this.$router.push(locationTo)
      } else {
        this.$router.push('/')
      }
    },

    redirectByQueryForAdmin() {
      const redirectTo = this.$route.query.redirect
      this.$router.replace(redirectTo ? redirectTo : '/admin')
    },

    async signInWithOAuth(providerName) {
      switch (providerName) {
        case 'google.com':
          await this.signInWithOAuthGoogle()
          break
        //case 'twitter.com':
        //  provider = new firebase.auth.TwitterAuthProvider()
        //  break
        //case 'facebook.com':
        //  provider = new firebase.auth.FacebookAuthProvider()
        //  break
        default:
          return
      }
    },

    async signInWithOAuthGoogle() {
      try {
        this.$store.dispatch('setLoading', true)
        const res = await Firebase.signInWithOAuthGoogle()
        const credential = GoogleAuthProvider.credentialFromResult(res)
        const token = credential.accessToken
        const user = res.user
        this.$store.dispatch('setUser', user)
        this.$store.dispatch('setLoading', false)
        this.redirectByQueryForUser()
      } catch (err) {
        console.log(err);//!!!!!!
        this.$store.dispatch('setLoading', false)
        this.handleApiError(err, this.$t('msg["Sign In failed"]'))
      }
    },

    keyDownEnter: function(event) {
      if (event.keyCode !== 13) return
      this.signIn()
    },

    setErrors: function(errors) {
      const keys = Object.keys(this.errors)
      errors.map(err => {
        const field = err.param
        this.initErrors(field)
        this.errors[field].push(err.msg)
      })
    },

    validateAll: function() {
      ['email', 'password'].map(field => {
        this.validate(field)
      })
    },

    validate: function(field) {
      const key = 'validate' + str.capitalize(field)
      this[key]()
    },

    validateEmail: function() {
      this.initError('email')
      if (this.checkEmpty(this.email)) this.errors.email.push(this.$t('msg["Email is required"]'))
      if (!str.checkEmail(this.email)) this.errors.email.push(this.$t('msg["Email is not valid"]'))
    },

    validatePassword: function() {
      this.initError('password')
      if (this.checkEmpty(this.password)) {
        this.errors.password.push(this.$t('msg["Password is required"]'))
      }
      if (this.password.length < 6) {
        this.errors.password.push(this.$t('msg["Password must be at least 6 characters"]'))
      }
    },

    initError: function(field) {
      this.$set(this.errors, field, [])
    },

    initErrors: function(field) {
      const keys = Object.keys(this.errors)
      if (!this.inArray(field, keys)) {
        this.initError(field)
      }
    },

    throwReject (err) { return Promise.reject(err) }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.signin {
}
</style>
